/**
 * The running-job strip, in the app header (gaps plan §8).
 *
 * It lives in the header rather than on the gaps screen for one reason: the
 * requirement is that a person can **leave the page**. A progress bar that
 * disappears when you navigate away reads as "the work stopped", and no
 * amount of reassuring text on the screen you just left can fix that. Here
 * it follows you, and clicking it goes back to where the work is.
 *
 * It polls the job list rather than holding a stream open: it is on every
 * screen, most of the time there is nothing running, and an idle EventSource
 * per tab for a feature that is off by default is not a good trade. Once
 * something *is* running, `JobPanel` opens the stream.
 */
import { useQuery } from '@tanstack/react-query'
import { Link } from 'wouter'
import type { JobRecord } from '@server/jobs/store.ts'
import { api, keys } from '~/lib/api.ts'
import { useJobStream } from './job-panel.tsx'
import { useT } from '~/i18n/index.tsx'
import { count, duration } from '~/i18n/format.ts'
import { Bar } from '~/components/ui/primitives.tsx'
import { useSuperadmin } from '~/lib/superadmin.ts'

const TERMINAL = new Set(['done', 'failed', 'cancelled', 'interrupted'])

export function JobStrip() {
  const superadmin = useSuperadmin()
  const { data } = useQuery({
    queryKey: keys.jobs(),
    queryFn: () => api<{ jobs: JobRecord[]; enabled: boolean }>('/api/jobs'),
    // Cheap: an in-memory list, no database. Frequent enough that a job
    // started in another tab shows up here without a reload.
    refetchInterval: 5_000,
    staleTime: 0,
    // No polling at all in the default view: the strip is hidden, so the
    // request would be a timer running for something nobody can see.
    enabled: superadmin,
  })

  const active = data?.jobs.find((j) => !TERMINAL.has(j.status))
  if (!superadmin || !active) return null
  return <ActiveStrip job={active} />
}

function ActiveStrip({ job: initial }: { job: JobRecord }) {
  const { t, locale } = useT()
  const { job } = useJobStream(initial)
  const step = job.steps[job.stepIndex]
  const etaMs = (job as JobRecord & { etaMs?: number | null }).etaMs ?? null
  const pct = step?.total && step.total > 0 ? step.done / step.total : null

  const href = job.symbol ? `/c/${job.symbol}?tab=gaps` : '/results'

  return (
    <Link
      href={href}
      className={
        'flex min-w-0 items-center gap-2 rounded-[var(--radius-control)] ' +
        'border border-[var(--accent)]/40 bg-[var(--accent-soft)] px-2 py-1 ' +
        'text-[length:var(--text-micro)] text-[var(--text)] transition-colors duration-75 ' +
        'hover:border-[var(--accent)]'
      }
      title={job.label}
    >
      <span className="relative flex h-1.5 w-1.5 shrink-0">
        <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[var(--accent)] opacity-60 motion-reduce:animate-none" />
        <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[var(--accent)]" />
      </span>
      <span className="hidden min-w-0 max-w-[16rem] truncate sm:inline">{job.label}</span>
      {step && (
        <span className="font-[family-name:var(--font-mono)] text-[var(--text-muted)]">
          {step.phase}
        </span>
      )}
      {pct !== null && <Bar value={pct} cells={6} state="accent" />}
      {step?.total !== null && step && (
        <span className="tnum text-[var(--text-muted)]">
          {count(step.done, locale)}/{count(step.total!, locale)}
        </span>
      )}
      {etaMs !== null && (
        <span className="tnum hidden text-[var(--text-muted)] md:inline">
          {t('job.remaining', { d: duration(etaMs, locale) })}
        </span>
      )}
    </Link>
  )
}
